import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  X, CreditCard, Lock, Shield,
  ChevronRight, Loader2, CheckCircle2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

// Payment method icons as SVG components
const VisaIcon = () => (
  <svg viewBox="0 0 48 48" className="h-8 w-12">
    <rect fill="#1A1F71" width="48" height="48" rx="4"/>
    <path fill="#fff" d="M19.5 31h-3.2l2-12.4h3.2L19.5 31zm13.3-12.1c-.6-.3-1.6-.5-2.9-.5-3.2 0-5.4 1.7-5.4 4.1 0 1.8 1.6 2.8 2.8 3.4 1.2.6 1.6 1 1.6 1.5 0 .8-1 1.2-1.9 1.2-1.3 0-1.9-.2-3-.7l-.4-.2-.4 2.7c.7.3 2.1.6 3.5.6 3.4 0 5.6-1.7 5.6-4.2 0-1.4-.8-2.5-2.7-3.4-1.1-.6-1.8-.9-1.8-1.5 0-.5.6-1 1.8-1 1 0 1.8.2 2.4.5l.3.1.5-2.6zm8.4-.3h-2.5c-.8 0-1.4.2-1.7 1l-4.8 11.4h3.4l.7-1.9h4.1l.4 1.9h3L41.2 18.6zm-4.9 7.8l1.3-3.5.7 3.5h-2zM16.3 18.6l-3 8.4-.3-1.6c-.6-1.9-2.3-4-4.2-5l2.9 10.6h3.4l5.1-12.4h-3.9z"/>
    <path fill="#F9A533" d="M10.5 18.6H5.1l-.1.3c4 1 6.7 3.5 7.8 6.5l-1.1-5.7c-.2-.8-.8-1-1.2-1.1z"/>
  </svg>
);

const MastercardIcon = () => (
  <svg viewBox="0 0 48 48" className="h-8 w-12">
    <rect fill="#000" width="48" height="48" rx="4"/>
    <circle fill="#EB001B" cx="18" cy="24" r="10"/>
    <circle fill="#F79E1B" cx="30" cy="24" r="10"/>
    <path fill="#FF5F00" d="M24 16.8c2.4 1.8 4 4.6 4 7.7s-1.6 5.9-4 7.7c-2.4-1.8-4-4.6-4-7.7s1.6-5.9 4-7.7z"/>
  </svg>
);

const PayPalIcon = () => (
  <svg viewBox="0 0 48 48" className="h-8 w-12">
    <rect fill="#003087" width="48" height="48" rx="4"/>
    <path fill="#fff" d="M35.8 16.2c-.5-2.6-2.6-4.2-5.8-4.2h-8.6c-.5 0-1 .4-1.1.9l-3.5 22.3c-.1.4.2.8.7.8h5l1.2-7.8-.1.2c.1-.5.5-.9 1.1-.9h2.2c4.4 0 7.9-1.8 8.9-7 0-.2 0-.3.1-.5-.1 0-.1 0 0 0 .3-1.6.1-2.8-.1-3.8z"/>
    <path fill="#009cde" d="M36.9 20c-1 5.2-4.5 7-8.9 7H25.8c-.5 0-1 .4-1.1.9l-1.5 9.3c-.1.4.2.7.6.7h4.2c.5 0 .9-.3 1-.8l.1-.2.8-5 .1-.3c.1-.5.5-.8 1-.8h.6c4 0 7.1-1.6 8-6.3.4-2-.1-3.6-1.2-4.7-.2-.3-.5-.5-.8-.7.1.3.2.6.3.9z"/>
  </svg>
);

const ApplePayIcon = () => (
  <svg viewBox="0 0 48 48" className="h-8 w-12">
    <rect fill="#000" width="48" height="48" rx="4"/>
    <path fill="#fff" d="M14.5 17.8c-.6.7-1.5 1.2-2.4 1.1-.1-.9.3-1.9.8-2.5.6-.7 1.5-1.2 2.3-1.2.1 1-.3 1.9-.7 2.6zm.7 1.3c-1.3-.1-2.5.8-3.1.8-.6 0-1.6-.7-2.7-.7-1.4 0-2.7.8-3.4 2.1-1.5 2.5-.4 6.3 1 8.4.7 1 1.5 2.2 2.6 2.1 1-.1 1.4-.7 2.7-.7 1.2 0 1.6.7 2.7.7 1.1 0 1.8-1 2.5-2.1.8-1.2 1.1-2.3 1.1-2.4 0 0-2.2-.8-2.2-3.2 0-2 1.6-2.9 1.7-3-1-1.4-2.4-1.6-2.9-1.6v.6zm9.3-2.2v14.8h2.3v-5.1h3.2c2.9 0 5-2 5-5s-2-4.9-4.9-4.9h-5.6v.2zm2.3 2h2.6c2 0 3.1 1.1 3.1 2.9 0 1.9-1.1 2.9-3.2 2.9h-2.5v-5.8zm13.7 13c1.4 0 2.8-.7 3.4-1.9h.1v1.8h2.1V24.2c0-2.2-1.7-3.5-4.3-3.5-2.4 0-4.2 1.4-4.3 3.3h2.1c.2-.9 1-1.5 2.1-1.5 1.4 0 2.1.6 2.1 1.8v.8l-2.8.2c-2.6.2-4 1.2-4 3.1 0 2 1.5 3.3 3.5 3.3v.2zm.6-1.8c-1.2 0-2-.6-2-1.5 0-.9.7-1.5 2.1-1.6l2.5-.2v.8c0 1.5-1.2 2.5-2.6 2.5z"/>
  </svg>
);

const GooglePayIcon = () => (
  <svg viewBox="0 0 48 48" className="h-8 w-12">
    <rect fill="#fff" width="48" height="48" rx="4" stroke="#e0e0e0"/>
    <path fill="#4285F4" d="M24.5 24.8v-3.5h8.3c.1.5.1 1.1.1 1.7 0 5.3-3.5 9-8.8 9-5.1 0-9.2-4.1-9.2-9.2s4.1-9.2 9.2-9.2c2.5 0 4.6.9 6.2 2.4l-2.5 2.5c-1-.9-2.2-1.4-3.7-1.4-3.2 0-5.7 2.6-5.7 5.8s2.5 5.8 5.7 5.8c2.9 0 4.8-1.7 5.3-4h-4.9z"/>
  </svg>
);

export default function PaymentModal({ isOpen, onClose, bill, paymentAmount, onPaymentSuccess, token }) {
  const { t } = useTranslation();
  const [selectedMethod, setSelectedMethod] = useState('card');
  const [processing, setProcessing] = useState(false);
  const [success, setSuccess] = useState(false);

  const paymentMethods = [
    { id: 'card', name: 'Credit Card', icon: <CreditCard className="h-5 w-5" /> },
    { id: 'visa', name: 'Visa', icon: <VisaIcon /> },
    { id: 'mastercard', name: 'Mastercard', icon: <MastercardIcon /> },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setProcessing(true);

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const originUrl = window.location.origin;

      const response = await fetch(`${BACKEND_URL}/api/payments/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          bill_id: bill.id,
          origin_url: originUrl
        })
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.detail || 'Payment failed');
      }

      const data = await response.json();

      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      setProcessing(false);
      toast.error(error.message || 'Payment failed. Please try again.');
    }
  };

  // Use the paymentAmount prop, fallback to bill amount
  const displayAmount = paymentAmount || bill?.amount || 0;
  const isPartialPayment = paymentAmount && bill?.amount && paymentAmount < bill.amount;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden animate-scaleIn">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold flex items-center gap-2">
              <Lock className="h-5 w-5" />
              {t('secure_payment') || 'Secure Payment'}
            </h2>
            <button
              onClick={onClose}
              className="hover:bg-white/20 p-2 rounded-full transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          
          {/* Amount */}
          <div className="bg-white/10 rounded-2xl p-4">
            <p className="text-blue-100 text-sm">{t('amount_to_pay') || 'Amount to Pay'}</p>
            <p className="text-4xl font-extrabold">₪{displayAmount.toFixed(2)}</p>
            {isPartialPayment && (
              <p className="text-yellow-300 text-sm mt-1">
                Partial payment (Total: ₪{bill?.amount?.toFixed(2)})
              </p>
            )}
            <p className="text-blue-200 text-sm mt-1">{bill?.provider} - {bill?.description}</p>
          </div>
        </div>

        {success ? (
          // Success State
          <div className="p-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-scaleIn">
              <CheckCircle2 className="h-10 w-10 text-green-600" />
            </div>
            <h3 className="text-2xl font-bold text-slate-900 mb-2">Payment Successful!</h3>
            <p className="text-slate-600">Your payment has been processed successfully.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Payment Methods */}
            <div>
              <Label className="text-sm font-semibold text-slate-700 mb-3 block">
                {t('payment_method') || 'Payment Method'}
              </Label>
              <div className="grid grid-cols-3 gap-2">
                {paymentMethods.map((method) => (
                  <button
                    key={method.id}
                    type="button"
                    onClick={() => setSelectedMethod(method.id)}
                    className={`p-3 rounded-xl border-2 transition-all flex flex-col items-center gap-1 ${
                      selectedMethod === method.id
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    {method.icon}
                    <span className="text-xs font-medium text-slate-600">{method.name}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Stripe Checkout Info */}
            <div className="bg-blue-50 rounded-xl p-4 text-center animate-fadeIn">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Shield className="h-5 w-5 text-blue-600" />
                <span className="font-semibold text-blue-800">Stripe Secure Checkout</span>
              </div>
              <p className="text-slate-600 text-sm">
                You will be redirected to Stripe's secure payment page to complete your payment.
              </p>
            </div>

            {/* Security badges */}
            <div className="flex items-center justify-center gap-4 py-2 text-xs text-slate-500">
              <span className="flex items-center gap-1">
                <Shield className="h-4 w-4 text-green-600" />
                SSL Secured
              </span>
              <span>256-bit Encryption</span>
              <span>PCI Compliant</span>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={processing}
              className="w-full h-14 text-lg font-bold rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-lg shadow-blue-500/30"
            >
              {processing ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Processing...
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Lock className="h-5 w-5" />
                  Pay ₪{displayAmount.toFixed(2)}
                  <ChevronRight className="h-5 w-5" />
                </div>
              )}
            </Button>

            <p className="text-center text-xs text-slate-500">
              By clicking Pay, you agree to our Terms of Service and Privacy Policy
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
